import React from "react";
import type { Theme } from "../lib/theme";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

/**
 * Choosing what a bid package actually covers.
 *
 * Cost codes, not divisions. A package is let under 06-100; budgeting it
 * against the whole of Division 06 compares a framing bid against framing
 * plus every piece of millwork in the job, and on the sandbox project that
 * reported a framing package 53% under budget.
 *
 * The division checkbox is still there and still takes the whole division in
 * one click, because for Division 03 that genuinely is the package.
 *
 * Lives in one file because the sidebar draws this twice -- once for the list
 * view and once for the tree -- and two copies of a picker drift apart.
 */
export default function ScopeWorkPicker({
  T, divisions, selected, setSelected, maxHeight = 180,
}: {
  T: Theme;
  divisions: any[];
  /** Selected subcategory ids. */
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  maxHeight?: number;
}) {
  return (
    <>
      <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 4 }}>What is being bid:</div>
      <div style={{ maxHeight, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1, marginBottom: 6 }}>
        {(divisions || []).map(d => {
          const subs = d.subcategories || [];
          const ids: string[] = subs.map((x: any) => x.id);
          const allOn = ids.length > 0 && ids.every(id => selected.includes(id));
          const someOn = !allOn && ids.some(id => selected.includes(id));
          return (
            <div key={d.id}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10,
                color: T.textMuted, cursor: "pointer", padding: "2px 0", fontWeight: 600 }}>
                <input type="checkbox" checked={allOn}
                  ref={el => { if (el) el.indeterminate = someOn; }}
                  onChange={e => setSelected(p => e.target.checked
                    ? [...new Set([...p, ...ids])]
                    : p.filter(x => !ids.includes(x)))}
                  style={{ accentColor: T.borderAccent }} />
                <span style={{ color: T.textWeak }}>{d.code}</span> {d.name}
              </label>
              {subs.map((sub: any) => (
                <label key={sub.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10,
                  color: T.textTertiary, cursor: "pointer", padding: "1px 0 1px 18px" }}>
                  <input type="checkbox" checked={selected.includes(sub.id)}
                    onChange={e => setSelected(p => e.target.checked ? [...p, sub.id] : p.filter(x => x !== sub.id))}
                    style={{ accentColor: T.borderAccent }} />
                  <span style={{ color: T.textWeak, fontVariantNumeric: "tabular-nums" }}>{sub.costCode}</span> {sub.name}
                </label>
              ))}
            </div>
          );
        })}
        {(!divisions || divisions.length === 0) && (
          <div style={{ fontSize: 10, color: T.textWeak, fontStyle: "italic", padding: "6px 0", fontFamily: FONT }}>
            No divisions in this estimate yet.
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The divisions a set of subcategories belongs to.
 *
 * A scope keeps divisionIds populated so everything that still reads it --
 * the Scope of Work generator, buyout matching -- keeps working; subIds is
 * what decides the budget.
 */
export const divisionsOf = (divisions: any[], subIds: string[]): string[] =>
  [...new Set((divisions || [])
    .filter(d => (d.subcategories || []).some((x: any) => subIds.includes(x.id)))
    .map(d => d.id))];
